/**
 * Calculates a price range based on the asking and estimated prices.
 *
 * Handles the following:
 *  - If `estimatedPrice <= askingPrice`: Follows Condition 1.
 *  - If `estimatedPrice > askingPrice`: Follows Condition 2.
 *  - Handles edge cases like invalid inputs, zero values, and large numbers.
 *
 * @param askingPrice - The asking price of the property.
 * @param estimatedPrice - The estimated price of the property.
 * @returns An object containing `startRange` and `endRange`. Defaults to `{ startRange: 0, endRange: 0 }` for invalid inputs.
 */
function calculateSellingRange(
  askingPrice: number,
  estimatedPrice: number
): { startRange: number; endRange: number } {
  // Default range for invalid inputs
  const defaultRange = { startRange: 0, endRange: 0 };
  
  if (askingPrice < 0 || estimatedPrice < 0) {
    console.error('Invalid input: Prices must be non-negative numbers.');
    return defaultRange;
  }
  
  if (askingPrice === 0 && estimatedPrice === 0) {
    console.warn('Both asking price and estimated price are zero. Defaulting to zero range.');
    return defaultRange;
  }
  
  let startRange = defaultRange?.startRange;
  let endRange = defaultRange?.endRange;
  
  if (estimatedPrice <= askingPrice) {
    // Step 1: Round down the estimated price to the nearest 1000 to calculate the start range
    startRange = Math.floor(estimatedPrice / 1000) * 1000;
    // Step 2: Calculate the difference between asking price and estimated price
    const difference = askingPrice - estimatedPrice;
    // Step 3: Find half the difference
    const halfDifference = difference / 2;
    // Step 4: Add the half difference to the asking price, round up to the nearest 1000
    endRange = Math.ceil((askingPrice + halfDifference) / 1000) * 1000;
  } else {
    // Step 1: Round down the asking price to the nearest 1000 to calculate the start range
    startRange = Math.floor(askingPrice / 1000) * 1000;
    // Step 2: Calculate the difference between estimated price and asking price
    const difference = estimatedPrice - askingPrice;
    // Step 3: Find half the difference
    const halfDifference = difference / 2;
    // Step 4: Add the half difference to the estimated price, round up to the nearest 1000
    endRange = Math.ceil((estimatedPrice + halfDifference) / 1000) * 1000;
  }

  return { startRange, endRange };
}
  
export { calculateSellingRange };